import './HeatingSettings.css';
import NumericInput from './NumericInput.js';
import React, { useState } from 'react';

export function RoomTable({ roomData }) {
  return (
    <div>
      <p></p>
      <h3>Rooms, devices and and temperature profiles</h3>
      <table className="room_table">
        <thead>
          <tr>
            <th>Room</th>
            <th>Temperature profile</th>
            <th>Thermometer</th>
            <th>Heaters</th>
          </tr>
        </thead>
        <tbody>
          {roomData.map((room, index) => (
            <tr key={index}>
              <td>{room.name}</td>
              <td>{room.profile}</td>
              <td>{room.thermometer}</td>
              <td>
                {room.heaters.map((heater, idx) => (
                  <div key={idx}>{heater}</div>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function BasicInfo({ basicInfo, onSchemeChange }) {
  return (
    <div>
      <h3>Basic Settings</h3>
      <h4>Choose Scheme</h4>
      {Object.entries(basicInfo.schemes)
        .filter(([key]) => key !== "active" && key !== "time_based_adjustments")
        .map(([key, value]) => (
          <span key={key} className="radio-option">
            <label>
              <input
                type="radio"
                value={key}
                checked={basicInfo.schemes.active === key}
                onChange={() => onSchemeChange(key)}
              />
              {value}
            </label>
          </span>
        ))}
    </div>
  );
}

export function TimeBasedAdjustments({ basicInfo, onUpdateAdjustment }) {
  const validateAdjustment = (adjustment) => {
    const { value, start_at, end_at, active_days } = adjustment;

    // Check if value is non-zero
    if (value === 0) {
      return { isValid: false, error: "Value set as 0" };
    }

    // Check if start_at and end_at are valid
    if (start_at === undefined || end_at === undefined) {
      return { isValid: false, error: "Start or End time is missing" };
    }
    if (start_at < 0 || start_at > 23 || end_at < 0 || end_at > 23) {
      return { isValid: false, error: "Start/End time must be between 0 and 23" };
    }

    // Check if active_days is a valid object
    if (!active_days || typeof active_days !== "object") {
      return { isValid: false, error: "Active days configuration is missing or invalid" };
    }

    // Check if at least one day is active
    const hasActiveDay = Object.values(active_days).some((isActive) => isActive);
    if (!hasActiveDay) {
      return { isValid: false, error: "At least one active day must be selected" };
    }

    // All validations passed
    return { isValid: true, error: null };
  };

  const handleFieldChange = (id, field, value) => {
    const adjustment = basicInfo.time_based_adjustments[id];
    const updatedAdjustment = {
      ...adjustment,
      [field]: value,
    };
    onUpdateAdjustment(id, updatedAdjustment); // Notify the parent component
  };

  const handleDayToggle = (id, day) => {
    const adjustment = basicInfo.time_based_adjustments[id];
    const updatedActiveDays = {
      ...adjustment.active_days,
      [day]: !adjustment.active_days[day], // Toggle the active state of the day
    };
    const updatedAdjustment = {
      ...adjustment,
      active_days: updatedActiveDays,
    };
    onUpdateAdjustment(id, updatedAdjustment); // Notify the parent component
  };

  const timeBasedAdjustments = basicInfo?.time_based_adjustments || {};

  return (
    <div>
      {timeBasedAdjustments && Object.keys(timeBasedAdjustments).length !== 0 && (
        <div className="adjustments-table-container">
          <h4>Time-Based Adjustments</h4>
          <table className="profile-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Degrees (°C)</th>
                <th>Time</th>
                <th>Active Days</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(timeBasedAdjustments).map(([id, adjustment]) => {
                const { isValid, error } = validateAdjustment(adjustment);
                const activeDays = adjustment.active_days || {};

                return (
                  <tr key={id} className={isValid ? "" : "disabled"}>
                    <td>{adjustment.name}</td>
                    <td>
                      <NumericInput
                        value={adjustment.value}
                        onChange={(value) => handleFieldChange(id, "value", value)}
                        min={-5}
                        max={-1}
                        step={1}
                      />
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <NumericInput
                          value={adjustment.start_at}
                          onChange={(value) => handleFieldChange(id, "start_at", value)}
                          min={0}
                          max={23}
                          step={1}
                          style={{ width: '40px' }}
                        />
                        <span style={{ margin: '5px' }}>-</span>
                        <NumericInput
                          value={adjustment.end_at}
                          onChange={(value) => handleFieldChange(id, "end_at", value)}
                          min={0}
                          max={23}
                          step={1}
                          style={{ width: '40px' }}
                        />
                      </div>
                    </td>

                    <td>
                      {Object.entries(activeDays).map(([day, isActive]) => (
                        <label key={day} style={{ marginRight: "10px" }}>
                          <input
                            type="checkbox"
                            checked={isActive}
                            onChange={() => handleDayToggle(id, day)}
                          />
                          {day.substring(0, 3)}
                        </label>
                      ))}
                    </td>
                    <td>{isValid ? "" : `Warning: ${error}`}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function ProfileTable({ profileConfigs, basicInfo, onTimeBasedAdjustmentToggle, onToggleEnabled, onToggleOptimisationEnabled, onReserveChange, onReserveHeatingAfternoonChange, onPriceChange, onPriceDropChange, onThermostatSettingChange, onThermostatReserveHeatingChange }) {
  const isIphone = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  return (
    <div>
      {isIphone && (
        <div>
          <i>Some temperature profile features may have limited functionality on mobile devices.</i>
        </div>
      )}
      <div className="profile-tables-container">
        <p></p>
        <h3>Electricity price based room temperature profiles</h3>
        {profileConfigs.map((profile) => (
          <div key={profile.id} className="profile-table">
            <h4>{profile.name}</h4>
            <label>
              Enabled:
              <input
                type="checkbox"
                checked={profile.enabled}
                onChange={(e) => onToggleEnabled(profile.id, 'enabled', e.target.checked)}
                //disabled={true}
              />
            </label>
            {profile.optimisation_enabled !== undefined ? (
              <div>
                <label>
                  Habitaio control enabled:
                  <input
                    type="checkbox"
                    checked={profile.optimisation_enabled}
                    onChange={(e) => onToggleOptimisationEnabled(profile.id, 'optimisation_enabled', e.target.checked)}
                    //disabled={true}
                  />
                </label>
              </div>
            ) : null}
            <table className="profile-table">
              <thead>
                <tr>
                  <th>Hourly electricity price ranges [c/kWh]</th>
                  {profile.price_ranges[0] && Object.keys(profile.price_ranges[0])
                    .filter(key => key !== 'id' && key !== 'range')
                    .map(scheme => (
                      <th key={scheme}>Target temperature {scheme.toUpperCase()} (°C)</th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {profile.price_ranges.map((range, index) => (
                  <tr key={range.id}>
                    <td>{range.range}</td>
                    {Object.keys(range)
                      .filter(key => key !== 'id' && key !== 'range')
                      .map(scheme => (
                        <td key={scheme}>
                          <NumericInput
                            value={range[scheme]}
                            onChange={(value) => onPriceChange(profile.id, range.id, scheme, value)}
                            min={7}
                            max={40}
                            step={0.5}
                          />
                        </td>
                      ))}
                  </tr>
                ))}
              </tbody>
            </table>

            {profile.time_based_adjustments !== undefined ? (
              <table className="profile-table">
                <thead>
                  <tr>
                    <th>Time-Based Adjustments</th>
                    {Object.keys(basicInfo.schemes ? basicInfo.schemes : {}).map((scheme, index) => (
                      scheme.startsWith("scheme_1") && (
                        <th key={index}>Enabled for {basicInfo.schemes[scheme].toUpperCase()}</th>
                      )
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(basicInfo.time_based_adjustments).map(([adjustmentId, adjustmentDetails]) => (
                    <tr key={adjustmentId}>
                      <td>{adjustmentDetails.name}</td>
                      {Object.keys(basicInfo.schemes).map((scheme) => (
                        scheme.startsWith("scheme_1") && (
                          <td key={`${adjustmentId}-${scheme}`}>
                            <input
                              type="checkbox"
                              checked={!!profile.time_based_adjustments?.[adjustmentId]?.[scheme]}
                              onChange={(e) =>
                                onTimeBasedAdjustmentToggle(
                                  profile.id,
                                  `time_based_adjustments.${adjustmentId}.${scheme}`,
                                  e.target.checked
                                )
                              }
                            />
                          </td>
                        )
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}

            {profile.reserve_heating && profile.reserve_heating.hours !== undefined && profile.reserve_heating.reserve_degrees !== undefined ? (
              <table className="profile-table">
                <thead>
                  <tr>
                    <th>Spot price based Reserve Heating</th>
                    <th>Hours</th>
                    <th>Degrees (°C)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Increase the target temperature during the lowest spot price <i>hours</i> by the <i>degrees</i> </td>
                    <td>
                      <NumericInput
                        value={profile.reserve_heating.hours}
                        onChange={(value) => onReserveChange(profile.id, 'hours', value)}
                        min={0}
                        max={24}
                        step={1}
                      />
                    </td>
                    <td>
                      <NumericInput
                        value={profile.reserve_heating.reserve_degrees}
                        onChange={(value) => onReserveChange(profile.id, 'reserve_degrees', value)}
                        min={0}
                        max={10}
                        step={0.5}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            ) : null}

            {profile.reserve_heating_afternoon && (
              <table className="profile-table">
                <thead>
                  <tr>
                    <th>Spot price based Reserve Heating for Afternoons</th>
                    <th>Hours</th>
                    <th>Degrees (°C)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Between 12 - 17, Increase the target temperature during the lowest spot price <i>hours</i> by the <i>degrees</i> </td>
                    <td>
                      <NumericInput
                        value={profile.reserve_heating_afternoon.hours}
                        onChange={(value) => onReserveHeatingAfternoonChange(profile.id, 'reserve_heating_afternoon', 'hours', value)}
                        min={0}
                        max={24}
                        step={1}
                      />
                    </td>
                    <td>
                      <NumericInput
                        value={profile.reserve_heating_afternoon.reserve_degrees}
                        onChange={(value) => onReserveHeatingAfternoonChange(profile.id, 'reserve_heating_afternoon', 'reserve_degrees', value)}
                        min={0}
                        max={10}
                        step={0.5}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            )}

            {profile.price_based_drops && profile.price_based_drops.reference_price_name !== undefined ? (
              <table className="profile-table">
                <thead>
                  <tr>
                    <th>Spot price based Temperature Adjustments</th>
                    {Object.keys(profile.price_based_drops)
                      .filter(key => key !== 'reference_price_name')
                      .map(scheme => (
                        <th key={scheme}>Temperature change on {scheme.toUpperCase()} (°C)</th>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Change the target temperature when spot price is above <i>{profile.price_based_drops.reference_price_name}</i> by <i>Degrees (°C)</i></td>
                    {Object.entries(profile.price_based_drops)
                      .filter(([scheme]) => scheme !== 'reference_price_name')
                      .map(([scheme, dropValue]) => (
                        <td key={scheme}>
                          <NumericInput
                            value={dropValue}
                            onChange={(value) => onPriceDropChange(profile.id, scheme, value)}
                            min={-5}
                            max={5}
                            step={0.5}
                          />
                        </td>
                      ))}
                  </tr>
                </tbody>
              </table>
            ) : null}

            {/* Thermostat Settings */}
            {profile.thermostat_settings && (
              <div>
                <table className="profile-table">
                  <thead>
                    <tr>
                      <th>Thermostat Specific Settings</th>
                      <th>Parameter</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(profile.thermostat_settings).map(([thermostatId, settings]) => (
                      <React.Fragment key={thermostatId}>
                        {/* Render the thermostat name */}
                        <tr>
                          <td colSpan="3"><b>{settings.name}</b></td>
                        </tr>
                        {/* Render the settings */}
                        {Object.entries(settings)
                          .filter(([key]) => key !== 'name') // Exclude the 'name' property from nested parameters
                          .map(([key, setting]) => (
                            <React.Fragment key={key}>
                              {/* Handle pv_controls separately */}
                              {key === 'pv_controls' && Object.keys(setting).length > 0 ? (
                                <React.Fragment>
                                  <tr>
                                    <td>Photovoltaics Settings</td>
                                    <td colSpan="2"></td>
                                  </tr>
                                  {Object.entries(setting).map(([paramKey, paramValue]) => (
                                    <tr key={paramKey}>
                                      <td></td>
                                      <td>
                                        {paramKey === 'enabled' 
                                          ? 'Enabled'
                                          : `On excess power, apply temperature for ${paramKey.toUpperCase()}`
                                        }
                                      </td>
                                      <td>
                                        {typeof paramValue === 'boolean' ? (
                                          <input
                                            type="checkbox"
                                            checked={paramValue}
                                            onChange={(e) =>
                                              onThermostatSettingChange(
                                                profile.id,
                                                thermostatId,
                                                key,
                                                { [paramKey]: e.target.checked }
                                              )
                                            }
                                          />
                                        ) : typeof paramValue === 'number' ? (
                                          <NumericInput
                                            value={paramValue}
                                            onChange={(value) =>
                                              onThermostatSettingChange(
                                                profile.id,
                                                thermostatId,
                                                key,
                                                { [paramKey]: parseFloat(value) || 0 }
                                              )
                                            }
                                            min={15}
                                            max={35}
                                            step={0.5}
                                          />
                                        ) : (
                                          <span>{paramValue}</span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </React.Fragment>
                              ) : key === 'manual_override' && Object.keys(setting).length > 0 ? (
                                <React.Fragment>
                                  <tr>
                                    <td>Manual Override</td>
                                    <td colSpan="2"></td>
                                  </tr>
                                  {Object.entries(setting).map(([paramKey, paramValue]) => (
                                    <tr key={paramKey}>
                                      <td></td>
                                      <td>
                                        {paramKey === 'enabled' 
                                          ? 'Enabled'
                                          : paramKey === 'minutes'
                                            ? 'Minutes'
                                            : paramKey
                                        }
                                      </td>
                                      <td>
                                        {typeof paramValue === 'boolean' ? (
                                          <input
                                            type="checkbox"
                                            checked={paramValue}
                                            onChange={(e) =>
                                              onThermostatSettingChange(
                                                profile.id,
                                                thermostatId,
                                                key,
                                                { [paramKey]: e.target.checked }
                                              )
                                            }
                                          />
                                        ) : typeof paramValue === 'number' ? (
                                          <NumericInput
                                            value={paramValue}
                                            onChange={(value) =>
                                              onThermostatSettingChange(
                                                profile.id,
                                                thermostatId,
                                                key,
                                                { [paramKey]: parseFloat(value) || 0 }
                                              )
                                            }
                                            min={1}
                                            max={180}
                                            step={1}
                                          />
                                        ) : (
                                          <span>{paramValue}</span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </React.Fragment>
                              ) : key !== 'pv_controls' && key !== 'manual_override' ? (
                                <React.Fragment>
                                  <tr>
                                    <td>{setting.name}</td>
                                    <td colSpan="2"></td>
                                  </tr>
                                  {Object.entries(setting)
                                    .filter(([paramKey]) => !['id', 'type', 'name'].includes(paramKey)) // Exclude `id`, `type`, `name`
                                    .map(([paramKey, paramValue]) => (
                                      <tr key={paramKey}>
                                        <td></td>
                                        <td>
                                          {paramKey === 'dynamic' && typeof paramValue === 'boolean'
                                            ? 'Automatic mode enabled'
                                            : paramKey === 'value'
                                              ? 'Adjust temperature by degrees (°C)'
                                              : paramKey === 'stabilisation_period'
                                                ? 'Stabilisation period (minutes)'
                                                : paramKey}
                                        </td>
                                        <td>
                                          {typeof paramValue === 'boolean' ? (
                                            <input
                                              type="checkbox"
                                              checked={paramValue}
                                              onChange={(e) =>
                                                onThermostatSettingChange(
                                                  profile.id,
                                                  thermostatId,
                                                  key,
                                                  { [paramKey]: e.target.checked }
                                                )
                                              }
                                            />
                                          ) : typeof paramValue === 'number' ? (
                                            <NumericInput
                                              value={paramValue}
                                              onChange={(value) =>
                                                onThermostatSettingChange(
                                                  profile.id,
                                                  thermostatId,
                                                  key,
                                                  { [paramKey]: parseFloat(value) || 0 }
                                                )
                                              }
                                              min={paramKey === 'value' ? -15 : 0}
                                              max={paramKey === 'value' ? 15 : 60}
                                              step={paramKey === 'value' ? 0.5 : 1}
                                            />
                                          ) : (
                                            <span>{paramValue}</span>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                </React.Fragment>
                              ) : null}
                            </React.Fragment>
                          ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {profile.thermostat_reserve_heating && (
              <div>
                <table className="profile-table">
                  <thead>
                    <tr>
                      <th>Thermostat Specific Reserve Heating</th>
                      <th>% of day max price</th>
                      <th>Hours</th>
                      <th>Degrees (°C)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(profile.thermostat_reserve_heating).map(([key, thermostat]) => (
                      <tr key={key}>
                        {/* Using the key as the thermostat identifier */}
                        {thermostat.type === 'max_spot' ? (
                          <>
                            <td>{thermostat.name}</td>
                            <td>
                              <NumericInput
                                value={thermostat.limit * 100} // Convert to percentage
                                onChange={(value) =>
                                  onThermostatReserveHeatingChange(profile.id, 'thermostat_reserve_heating', key, 'limit', value / 100)
                                }
                                min={0}
                                max={50}
                                step={5}
                              />
                            </td>
                          </>
                        ) : (
                          <td colSpan="2">{thermostat.name}</td>
                        )}
                        <td>
                          <NumericInput
                            value={thermostat.hours}
                            onChange={(value) =>
                              onThermostatReserveHeatingChange(profile.id, 'thermostat_reserve_heating', key, 'hours', value)
                            }
                            min={0}
                            max={24}
                            step={1}
                          />
                        </td>
                        <td>
                          <NumericInput
                            value={thermostat.reserve_degrees}
                            onChange={(value) =>
                              onThermostatReserveHeatingChange(profile.id, 'thermostat_reserve_heating', key, 'reserve_degrees', value)
                            }
                            min={0}
                            max={5}
                            step={1}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}